import { tap, finalize } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';

import { Injectable } from '@angular/core';

import { AuthStore } from '@/app/core/store/auth';
import { AuthService, CredentialsInfo } from '@/app/api/auth';
import { NamedRouter, NamedRouterNavigationExtras } from '@/app/core/services/named-router';

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private TOKEN_COOKIE_KEY = 'token';

  constructor(
    private authStore: AuthStore,
    private namedRouter: NamedRouter,
    private authService: AuthService,
    private cookieService: CookieService,
  ) {}

  isAuthenticated() {
    return this.cookieService.check(this.TOKEN_COOKIE_KEY);
  }

  getToken() {
    return this.cookieService.get(this.TOKEN_COOKIE_KEY);
  }

  private _deleteCredentials() {
    this.cookieService.delete(this.TOKEN_COOKIE_KEY, '/');
    this.authStore.setAuthUser(null);
  }

  private _saveToken(tokenInfo: CredentialsInfo['token']) {
    this.cookieService.delete(this.TOKEN_COOKIE_KEY, '/');
    this.cookieService.set(this.TOKEN_COOKIE_KEY, tokenInfo.key, {
      expires: new Date(tokenInfo.refresh_expires_at),
      secure: true,
      sameSite: 'Strict',
      path: '/',
    });
  }

  private _saveCredentials(data: CredentialsInfo) {
    this.authStore.setAuthUser(data.user);
    this._saveToken(data.token);
  }

  login(email: string, password: string) {
    return this.authService.login(email, password).pipe(tap((credentialInfo) => this._saveCredentials(credentialInfo)));
  }

  navigateToLogin(extras?: NamedRouterNavigationExtras) {
    this._deleteCredentials();
    this.namedRouter.navigateByName('login', extras);
  }

  logout() {
    return this.authService.logout().pipe(finalize(() => this.navigateToLogin()));
  }

  refreshToken() {
    return this.authService.refreshToken().pipe(tap((credentialInfo) => this._saveCredentials(credentialInfo)));
  }
}
